import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errText } from "@/lib/api";
import { longDate } from "@/lib/eventMeta";

export const BriefSendDialog = ({ open, onOpenChange, date, recipients, onSent }) => {
  const [picked, setPicked] = useState([]);
  const [extra, setExtra] = useState("");
  const [sending, setSending] = useState(false);

  const toggle = (id) =>
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const send = async () => {
    const extras = extra
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!picked.length && !extras.length) return toast.error("Pick an associate or type an email");
    setSending(true);
    try {
      const { data } = await api.post("/brief/send", {
        date,
        recipient_ids: picked,
        extra_emails: extras,
      });
      toast.success(`Brief emailed to ${data.count} associate(s) with the PDF attached`);
      setExtra("");
      setPicked([]);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[75vh] overflow-y-auto" data-testid="brief-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Email the associate brief</DialogTitle>
          <DialogDescription className="text-sm">
            {longDate(date)} — the full brief goes in the email body and is attached as a PDF.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="label-caps">Saved associates</Label>
            {recipients.length ? (
              <div className="divide-y divide-border border border-border rounded-sm">
                {recipients.map((r) => (
                  <label
                    key={r.recipient_id}
                    data-testid={`send-recipient-${r.recipient_id}`}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                  >
                    <Checkbox
                      data-testid={`send-recipient-check-${r.recipient_id}`}
                      checked={picked.includes(r.recipient_id)}
                      onCheckedChange={() => toggle(r.recipient_id)}
                    />
                    <span className="min-w-0">
                      <span className="block text-base font-medium truncate">{r.name}</span>
                      <span className="block text-xs text-muted-foreground truncate">{r.email}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-3">
                No saved associates yet — add them in Settings, or type an address below.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="label-caps">Also send to</Label>
            <Input
              data-testid="send-extra-email"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="junior@chambers.com"
              className="rounded-sm h-12 text-base"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-full h-11"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="send-brief-confirm"
            onClick={send}
            disabled={sending}
            className="rounded-full h-11 px-6"
          >
            <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Send brief"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
